import { supabase as defaultClient } from "@/integrations/supabase/client";

export type CreateAccountInput = {
  name: string;
  support_email: string;
  locale?: string;
  domain?: string;
};

export type CreateUserInput = {
  name: string;
  email: string;
  password: string;
  display_name?: string;
};

export type LinkAccountUserInput = {
  user_id: number; // chatwoot user id
  role?: "administrator" | "agent";
};

export type ChatwootEntityType = "client" | "supplier" | "profile";

export class ChatwootSDK {
  constructor(private client = defaultClient) {}

  async createAccount(input: CreateAccountInput) {
    const { data, error } = await this.client.functions.invoke("chatwoot-create-account", {
      body: input,
    });
    if (error) throw error;
    console.log("[chatwoot] createAccount response", data);
    return data as { success: boolean; account_id: number };
  }

  async createUser(input: CreateUserInput) {
    const { data, error } = await this.client.functions.invoke("chatwoot-create-user", {
      body: input,
    });
    if (error) throw error;
    console.log("[chatwoot] createUser response", data);
    return data as { success: boolean; chatwoot_user_id: number; access_token?: string };
  }

  async linkAccountUser(input: LinkAccountUserInput) {
    const { data, error } = await this.client.functions.invoke("chatwoot-link-account-user", {
      body: { user_id: input.user_id, role: input.role ?? "administrator" },
    });
    if (error) throw error;
    console.log("[chatwoot] linkAccountUser response", data);
    return data as { success: boolean; result: { account_id: number; user_id: number; role: string } };
  }

  async unlinkAccountUser(user_id: number) {
    const { data, error } = await this.client.functions.invoke("chatwoot-unlink-account-user", {
      body: { user_id },
    });
    if (error) throw error;
    console.log("[chatwoot] unlinkAccountUser response", data);
    return data as { success: boolean };
  }

  async getUserSsoLink(user_id?: number) {
    const { data, error } = await this.client.functions.invoke("chatwoot-get-user-sso-link", {
      body: user_id ? { user_id } : {},
    });
    if (error) throw error;
    console.log("[chatwoot] getUserSsoLink response", data);
    return data as { success: boolean; url?: string };
  }

  async saveUserToken(token: string) {
    const { data, error } = await this.client.functions.invoke("chatwoot-save-user-token", {
      body: { token },
    });
    if (error) throw error;
    console.log("[chatwoot] saveUserToken response", data);
    return data as { success: boolean; chatwoot_user_id?: number | null; chatwoot_account_id?: number | null };
  }

  async syncContact(entity_type: ChatwootEntityType, entity_id: string) {
    const { data, error } = await this.client.functions.invoke("chatwoot-sync-contact", {
      body: { entity_type, entity_id },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    console.log("[chatwoot] syncContact response", data);
    return data as { success: boolean; chatwoot_contact_id?: number | null; action?: "create" | "update" };
  }

  async listAccountUsers(account_id: number) {
    const { data, error } = await this.client.functions.invoke("chatwoot-list-account-users", {
      body: { account_id },
    });
    if (error) throw error;
    if ((data as any)?.error) throw new Error((data as any).error);
    console.log("[chatwoot] listAccountUsers response", data);
    return data as { success: boolean; users: Array<{ account_id: number; user_id: number; role: string }> };
  }

  async appProxy<T = any>(params: { path: string; method?: "GET" | "POST" | "PATCH" | "DELETE"; token: string; query?: Record<string, any>; body?: any }) {
    try {
      const { data, error } = await this.client.functions.invoke("chatwoot-app-proxy", {
        body: params,
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      console.log("[chatwoot] appProxy response", (data as any).data);
      return (data as any).data as T;
    } catch (e: any) {
      const msg = String(e?.message || e);
      const isAborted = msg.includes("ERR_ABORTED") || e?.name === "AbortError";
      if (isAborted) {
        const { data, error } = await this.client.functions.invoke("chatwoot-app-proxy", { body: params });
        if (error) throw error;
        if ((data as any)?.error) throw new Error((data as any).error);
        console.log("[chatwoot] appProxy response(retry)", (data as any).data);
        return (data as any).data as T;
      }
      throw e;
    }
  }

  async listUserAccounts(user_id: number) {
    const { data, error } = await this.client.functions.invoke("chatwoot-list-user-accounts", {
      body: { user_id },
    });
    if (error) throw error;
    if ((data as any)?.error) throw new Error((data as any).error);
    console.log("[chatwoot] listUserAccounts response", data);
    return data as { success: boolean; accounts: Array<{ account_id: number; role: string; name?: string }> };
  }
}

export const getChatwootSDK = () => new ChatwootSDK();
